import { FC, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { RootState } from '../../../ducks/store';
import {
  NavSection,
  pinPanel,
  unpinPanel,
  clearDrillDown,
  setPanelOpen,
} from '../../../ducks/features/navPanelSlice';
import NavRailIcon from './NavRailIcon';
import NavPanel from './NavPanel';
import CoursesPanel from './panels/CoursesPanel';
import ReportsPanel from './panels/ReportsPanel';
import SearchPanel from './panels/SearchPanel';
import TeamsPanel from './panels/TeamsPanel';
import UsersPanel from './panels/UsersPanel';

import './NavRail.scss';

const Paths = {
  Dashboard: '/',
  Courses: '/admin/courses',
  Teams: '/admin/teams',
  Users: '/admin/users',
  GeneralReport: '/admin/reports/general',
  CLEReport: '/admin/reports/cle',
};

const Labels = {
  Home: 'Home',
  Courses: 'Courses',
  Reports: 'Reports',
  Teams: 'Teams',
  Users: 'Users',
} as const;

const sectionForPath = (pathname: string): NavSection | null => {
  const lower = pathname.toLowerCase();
  if (lower.startsWith('/admin/courses')) return NavSection.Content;
  if (lower.startsWith('/admin/reports')) return NavSection.Reports;
  if (lower.startsWith('/admin/teams')) return NavSection.Teams;
  if (lower.startsWith('/admin/users')) return NavSection.Users;
  return null;
};

const NavRail: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const { pinned, pinnedSection } = useSelector((state: RootState) => state.navPanel);

  const visibleSection = pinned ? pinnedSection : null;
  const isPanelOpen = pinned;

  useEffect(() => {
    dispatch(setPanelOpen(pinned));
  }, [pinned, dispatch]);

  const handleIconClick = useCallback(
    (section: NavSection, path: string) => {
      navigate(path);
      dispatch(pinPanel(section));
      dispatch(clearDrillDown(NavSection.Teams));
    },
    [navigate, dispatch],
  );

  const handleHamburgerClick = useCallback(() => {
    if (pinned) {
      dispatch(unpinPanel());
    } else {
      const section = sectionForPath(pathname) || NavSection.Content;
      dispatch(pinPanel(section));
    }
  }, [pinned, pathname, dispatch]);

  const handleClosePanel = useCallback(() => {
    dispatch(unpinPanel());
  }, [dispatch]);

  const teamsDrillDown = useSelector(
    (state: RootState) => state.navPanel.drillDown[NavSection.Teams],
  );

  const activeSection = sectionForPath(pathname);
  const isHome = pathname === Paths.Dashboard;
  const isSearchPinned = pinnedSection === NavSection.Search;
  const isCoursesVisible = visibleSection === NavSection.Content;
  const isReportsVisible = visibleSection === NavSection.Reports;
  const isTeamsVisible = visibleSection === NavSection.Teams;
  const isUsersVisible = visibleSection === NavSection.Users;
  const isSearchVisible = visibleSection === NavSection.Search;

  // Sync pinned section when route changes externally (e.g. TopNav links)
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    const pathnameChanged = prevPathnameRef.current !== pathname;
    prevPathnameRef.current = pathname;

    if (!pathnameChanged || !pinned || !activeSection) return;
    if (activeSection !== pinnedSection) {
      dispatch(pinPanel(activeSection));
    }
  }, [pathname, pinned, activeSection, pinnedSection, dispatch]);

  const sections = [
    {
      section: NavSection.Content,
      label: Labels.Courses,
      icon: 'school',
      outlined: true,
      children: <CoursesPanel visible={isCoursesVisible} />,
    },
    {
      section: NavSection.Reports,
      label: Labels.Reports,
      icon: 'assessment',
      outlined: true,
      children: <ReportsPanel />,
    },
    {
      section: NavSection.Teams,
      label: Labels.Teams,
      icon: 'group',
      outlined: true,
      children: <TeamsPanel visible={isTeamsVisible} />,
      backAction: teamsDrillDown
        ? {
            label: Labels.Teams,
            onBack: () => {
              dispatch(clearDrillDown(NavSection.Teams));
              navigate(Paths.Teams);
            },
          }
        : undefined,
    },
    {
      section: NavSection.Users,
      label: Labels.Users,
      icon: 'person_outline',
      children: <UsersPanel visible={isUsersVisible} />,
    },
    {
      section: NavSection.Search,
      label: 'Search',
      customHeader: true,
      children: <SearchPanel visible={isSearchVisible} onClose={handleClosePanel} />,
    },
  ];

  return (
    <div className="NavRail-container">
      <nav className="NavRail" aria-label="Main navigation">
        <NavRailIcon
          icon={pinned ? 'first_page' : 'menu'}
          label=""
          bordered
          disabled={isHome && !pinned}
          onClick={handleHamburgerClick}
          ariaExpanded={isPanelOpen}
          ariaControls="nav-panel"
        />
        <NavRailIcon
          icon="grid_view"
          label={Labels.Home}
          active={isHome}
          bordered
          onClick={() => {
            navigate(Paths.Dashboard);
            dispatch(unpinPanel());
          }}
        />
        <NavRailIcon
          icon="search"
          label="Search"
          active={isSearchPinned}
          bordered
          onClick={() => {
            if (isSearchPinned) {
              dispatch(unpinPanel());
            } else {
              dispatch(pinPanel(NavSection.Search));
            }
          }}
          ariaExpanded={isPanelOpen && isSearchVisible}
          ariaControls="nav-panel"
        />
        <NavRailIcon
          icon="school"
          label={Labels.Courses}
          outlined
          active={activeSection === NavSection.Content}
          section={NavSection.Content}
          onClick={() => handleIconClick(NavSection.Content, Paths.Courses)}
          ariaExpanded={isPanelOpen && isCoursesVisible}
          ariaControls="nav-panel"
        />
        <NavRailIcon
          icon="assessment"
          label={Labels.Reports}
          outlined
          active={activeSection === NavSection.Reports}
          section={NavSection.Reports}
          onClick={() => handleIconClick(NavSection.Reports, Paths.GeneralReport)}
          ariaExpanded={isPanelOpen && isReportsVisible}
          ariaControls="nav-panel"
        />
        <NavRailIcon
          icon="group"
          label={Labels.Teams}
          outlined
          active={activeSection === NavSection.Teams}
          section={NavSection.Teams}
          onClick={() => handleIconClick(NavSection.Teams, Paths.Teams)}
          ariaExpanded={isPanelOpen && isTeamsVisible}
          ariaControls="nav-panel"
        />
        <NavRailIcon
          icon="person_outline"
          label={Labels.Users}
          active={activeSection === NavSection.Users}
          section={NavSection.Users}
          onClick={() => handleIconClick(NavSection.Users, Paths.Users)}
          ariaExpanded={isPanelOpen && isUsersVisible}
          ariaControls="nav-panel"
        />
      </nav>
      <NavPanel
        open={isPanelOpen}
        pinned={pinned}
        visibleSection={visibleSection}
        sections={sections}
        onClose={handleClosePanel}
      />
    </div>
  );
};

export default NavRail;
